import React, { useState, useMemo } from "react";
import styled from "styled-components";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from "@tanstack/react-table";
import { GroupedInventoryItem } from "../store/inventoryStore";
import { Link } from "react-router-dom";

// Типы
interface SalesTableProps {
  data: GroupedInventoryItem[];
  isLoading?: boolean;
  onDelete: (sizeId: number) => void;
  formatPrice: (price: string) => string;
  formatMarketPrice: (price: string | number | undefined) => string;
}

// Стилизованные компоненты
const TableWrapper = styled.div`
  width: 100%;
  overflow: visible;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  gap: 8px;
  flex: 1;
  overflow: visible;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
`;

const FilterRow = styled.div`
  display: none;
  user-select: none;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 2fr 1.2fr 1fr 1fr 1fr 1fr;
    align-items: center;
    width: 100%;
    max-width: 100%;
    margin-bottom: 8px;
    column-gap: 6px;
    padding: 0 12px;
    box-sizing: border-box;
  }

  @media (min-width: 900px) {
    grid-template-columns: 2.2fr 1.4fr 1.1fr 1.1fr 1.1fr 1.1fr;
    column-gap: 10px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 2.4fr 1.6fr 1.2fr 1.2fr 1.2fr 1.2fr;
    column-gap: 12px;
  }

  @media (min-width: 1200px) {
    grid-template-columns: 2.6fr 1.6fr 1.2fr 1.2fr 1.2fr 1.2fr;
    column-gap: 14px;
  }

  @media (min-width: 1440px) {
    grid-template-columns: 2.8fr 1.8fr 1.3fr 1.3fr 1.3fr 1.3fr;
    column-gap: 16px;
  }

  @media (min-width: 1680px) {
    grid-template-columns: 3fr 2fr 1.4fr 1.4fr 1.4fr 1.4fr;
    column-gap: 18px;
    padding: 0 20px;
  }

  @media (min-width: 1920px) {
    grid-template-columns: 3.2fr 2.2fr 1.5fr 1.5fr 1.5fr 1.5fr;
    column-gap: 20px;
    padding: 0 24px;
  }

  @media (min-width: 2560px) {
    grid-template-columns: 3.5fr 2.4fr 1.6fr 1.6fr 1.6fr 1.6fr;
    column-gap: 24px;
    padding: 0 32px;
  }
`;

const FilterItem = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  width: 100%;
  gap: 2px;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  user-select: none;

  &:hover {
    background-color: ${(props) =>
      props.$clickable ? "rgba(96, 159, 148, 0.08)" : "transparent"};
  }

  &:first-child {
    justify-content: flex-start;
  }
`;

const FilterLabel = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #609f94;
  white-space: nowrap;
  flex-shrink: 0;
  user-select: none;

  @media (min-width: 768px) {
    font-size: 13px;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
  }
`;

const SortIcon = styled.div<{ direction: "asc" | "desc" | "none" }>`
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  flex-shrink: 0;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.direction === "none" ? 0.3 : 1)};

  ${(props) => {
    if (props.direction === "asc") {
      return `
        border-bottom: 6px solid #609F94;
        border-top: none;
      `;
    } else if (props.direction === "desc") {
      return `
        border-top: 6px solid #609F94;
        border-bottom: none;
      `;
    } else {
      return `
        border-bottom: 6px solid #609F94;
        border-top: none;
      `;
    }
  }}

  @media (min-width: 768px) {
    border-left-width: 5px;
    border-right-width: 5px;
    border-bottom-width: ${(props) =>
      props.direction === "asc" || props.direction === "none" ? "7px" : "0"};
    border-top-width: ${(props) => (props.direction === "desc" ? "7px" : "0")};
  }

  @media (min-width: 1024px) {
    border-left-width: 6px;
    border-right-width: 6px;
    border-bottom-width: ${(props) =>
      props.direction === "asc" || props.direction === "none" ? "8px" : "0"};
    border-top-width: ${(props) => (props.direction === "desc" ? "8px" : "0")};
  }
`;

const CardWithExpandWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: visible;
  box-sizing: border-box;
`;

const CardContainer = styled.div`
  background-color: #1a2e2e;
  border-radius: 12px;
  border: 1px solid #2c4a4a;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;

  &:hover {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
    transform: translateY(-2px);
  }
`;

const CardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  width: 100%;
  min-height: 80px;
  column-gap: 8px;
  padding: 12px;
  overflow: visible;
  box-sizing: border-box;

  @media (min-width: 768px) {
    grid-template-columns: 2fr 1.2fr 1fr 1fr 1fr 1fr;
    column-gap: 6px;
    padding: 12px;
    min-height: 60px;
    max-width: 100%;
    box-sizing: border-box;
  }

  @media (min-width: 900px) {
    grid-template-columns: 2.2fr 1.4fr 1.1fr 1.1fr 1.1fr 1.1fr;
    column-gap: 10px;
    min-height: 60px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 2.4fr 1.6fr 1.2fr 1.2fr 1.2fr 1.2fr;
    column-gap: 12px;
    min-height: 64px;
  }

  @media (min-width: 1200px) {
    grid-template-columns: 2.6fr 1.6fr 1.2fr 1.2fr 1.2fr 1.2fr;
    column-gap: 14px;
    min-height: 64px;
  }

  @media (min-width: 1440px) {
    grid-template-columns: 2.8fr 1.8fr 1.3fr 1.3fr 1.3fr 1.3fr;
    column-gap: 16px;
    min-height: 68px;
  }

  @media (min-width: 1680px) {
    grid-template-columns: 3fr 2fr 1.4fr 1.4fr 1.4fr 1.4fr;
    column-gap: 18px;
    padding: 12px 20px;
    min-height: 68px;
  }

  @media (min-width: 1920px) {
    grid-template-columns: 3.2fr 2.2fr 1.5fr 1.5fr 1.5fr 1.5fr;
    column-gap: 20px;
    padding: 12px 24px;
    min-height: 72px;
  }

  @media (min-width: 2560px) {
    grid-template-columns: 3.5fr 2.4fr 1.6fr 1.6fr 1.6fr 1.6fr;
    column-gap: 24px;
    padding: 12px 32px;
    min-height: 76px;
  }
`;

const MobileProductCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileProductHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const MobileProductInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid #2c4a4a;
`;

const MobileProductPrice = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #24ba7d;
`;

const MobileProductArticle = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 12px;
  color: #8b9ba8;
`;

const ProductInfo = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const ProductImage = styled.div`
  width: 50px;
  height: 50px;
  background-color: transparent;
  border-radius: 6px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  flex-shrink: 0;

  @media (min-width: 768px) {
    width: 70px;
    height: 70px;
    border-radius: 7px;
  }

  @media (min-width: 1024px) {
    width: 80px;
    height: 80px;
    border-radius: 8px;
  }

  @media (min-width: 1440px) {
    width: 96px;
    height: 96px;
    border-radius: 10px;
  }

  @media (min-width: 1920px) {
    width: 112px;
    height: 112px;
    border-radius: 12px;
  }
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
`;

const ProductName = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 1024px) {
    font-size: 13px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
  }

  @media (min-width: 1920px) {
    font-size: 15px;
  }
`;

const ProductModel = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 11px;
  color: #8b9ba8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 1024px) {
    font-size: 12px;
  }

  @media (min-width: 1920px) {
    font-size: 13px;
  }
`;

const ProductColor = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 10px;
  color: #609f94;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 1024px) {
    font-size: 11px;
  }

  @media (min-width: 1920px) {
    font-size: 12px;
  }
`;

const DesktopContent = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const SizeLabel = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 12px;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;

  @media (min-width: 1024px) {
    font-size: 13px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
  }
`;

const SizesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

const SizeChip = styled.span`
  background-color: #0e1f1f;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 11px;
    padding: 5px 9px;
  }

  @media (min-width: 1024px) {
    font-size: 12px;
    padding: 6px 10px;
  }

  @media (min-width: 1440px) {
    font-size: 13px;
    padding: 7px 11px;
  }

  @media (min-width: 1920px) {
    font-size: 14px;
    padding: 8px 12px;
  }
`;

const SizeCount = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-size: 11px;
  color: #609f94;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 12px;
  }

  @media (min-width: 1024px) {
    font-size: 13px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
  }

  @media (min-width: 1920px) {
    font-size: 15px;
  }
`;

const PriceLabel = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #24ba7d;
  text-align: center;

  @media (min-width: 1024px) {
    font-size: 13px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
  }

  @media (min-width: 1920px) {
    font-size: 15px;
  }
`;

const ArticleLabel = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 12px;
  color: #8b9ba8;
  text-align: center;

  @media (min-width: 1024px) {
    font-size: 13px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
  }
`;

const ExpandableContent = styled.div<{ $expanded: boolean }>`
  overflow: hidden;
  max-height: ${(props) => (props.$expanded ? "auto" : "0")};
  opacity: ${(props) => (props.$expanded ? "1" : "0")};
  transition: max-height 0.3s ease, opacity 0.3s ease;
  background-color: #132525;

  @media (max-width: 767px) {
    max-height: ${(props) => (props.$expanded ? "8000px" : "0")};
  }
`;

const SizeVariantContainer = styled.div`
  padding: 16px;
  border-top: 1px solid #2c4a4a;

  @media (min-width: 768px) {
    padding: 16px 20px;
  }

  @media (min-width: 1024px) {
    padding: 20px;
  }
`;

const SizeVariantContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const SizeVariantInfo = styled.div`
  flex: 1;
`;

const SizeVariantGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (min-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const InfoPill = styled.div`
  background-color: #0e1f1f;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (min-width: 768px) {
    padding: 10px 14px;
  }
`;

const InfoLabel = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-size: 10px;
  color: #609f94;
  font-weight: 400;

  @media (min-width: 768px) {
    font-size: 11px;
  }

  @media (min-width: 1024px) {
    font-size: 12px;
  }
`;

const InfoValue = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-size: 12px;
  color: #ffffff;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 13px;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
  }

  @media (min-width: 1440px) {
    font-size: 16px;
  }
`;

const ProductLink = styled(Link)`
  color: #24ba7d;
  text-decoration: none;
  font-family: "Ubuntu", sans-serif;
  font-size: 12px;
  font-weight: 500;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  @media (min-width: 768px) {
    font-size: 13px;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

// Компонент
const SalesTable: React.FC<SalesTableProps> = ({
  data,
  isLoading,
  onDelete,
  formatPrice,
  formatMarketPrice,
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sorting, setSorting] = useState<SortingState>([]);

  // Функция для группировки размеров
  const groupSizesByValue = (
    sizes: any[]
  ): { size: string; count: number }[] => {
    const grouped = sizes.reduce(
      (acc: { size: string; count: number }[], size: any) => {
        const existing = acc.find((item) => item.size === size.size);
        if (existing) {
          existing.count++;
        } else {
          acc.push({ size: size.size, count: 1 });
        }
        return acc;
      },
      [] as { size: string; count: number }[]
    );
    return grouped;
  };

  // Колонки таблицы
  const columns = useMemo<ColumnDef<GroupedInventoryItem>[]>(
    () => [
      {
        id: "product",
        accessorKey: "name",
        header: "Товар",
        cell: ({ row }) => {
          const product = row.original;
          return (
            <>
              <MobileProductCard>
                <MobileProductHeader>
                  <ProductImage
                    style={{ backgroundImage: `url(${product.imageUrl})` }}
                  />
                  <ProductDetails>
                    <ProductName>{product.name}</ProductName>
                    <ProductModel>{product.model}</ProductModel>
                    <ProductColor>{product.color}</ProductColor>
                  </ProductDetails>
                </MobileProductHeader>
                <SizesContainer>
                  {Array.isArray(product.sizes) &&
                    groupSizesByValue(product.sizes)
                      .slice(0, 8)
                      .map((groupedSize, index) => (
                        <React.Fragment key={index}>
                          <SizeChip>
                            {groupedSize.size}
                            {groupedSize.count > 1 && (
                              <span style={{ color: "#24BA7D" }}>
                                {" "}
                                x{groupedSize.count}
                              </span>
                            )}
                          </SizeChip>
                        </React.Fragment>
                      ))}
                  {Array.isArray(product.sizes) &&
                    groupSizesByValue(product.sizes).length > 8 && (
                      <SizeCount style={{ marginLeft: "4px" }}>
                        +{groupSizesByValue(product.sizes).length - 8}
                      </SizeCount>
                    )}
                </SizesContainer>
                <MobileProductInfo>
                  <MobileProductPrice>
                    {formatPrice(
                      product.priceRange.min === product.priceRange.max
                        ? `${product.priceRange.min} Р`
                        : `${product.priceRange.min} - ${product.priceRange.max} Р`
                    )}
                  </MobileProductPrice>
                  <MobileProductArticle>{product.article}</MobileProductArticle>
                </MobileProductInfo>
              </MobileProductCard>

              <DesktopContent>
                <ProductInfo>
                  <ProductImage
                    style={{ backgroundImage: `url(${product.imageUrl})` }}
                  />
                  <ProductDetails>
                    <ProductName>{product.name}</ProductName>
                    <ProductModel>{product.model}</ProductModel>
                    <ProductColor>{product.color}</ProductColor>
                  </ProductDetails>
                </ProductInfo>
              </DesktopContent>
            </>
          );
        },
      },
      {
        id: "sizes",
        accessorKey: "sizes",
        header: "Размеры",
        cell: ({ row }) => {
          const product = row.original;
          return (
            <DesktopContent>
              <SizeLabel>
                <SizesContainer>
                  {Array.isArray(product.sizes) &&
                    groupSizesByValue(product.sizes)
                      .slice(0, 12)
                      .map((groupedSize, index) => (
                        <React.Fragment key={index}>
                          <SizeChip>
                            {groupedSize.size}
                            {groupedSize.count > 1 && (
                              <span style={{ color: "#24BA7D" }}>
                                {" "}
                                x{groupedSize.count}
                              </span>
                            )}
                          </SizeChip>
                        </React.Fragment>
                      ))}
                  {Array.isArray(product.sizes) &&
                    groupSizesByValue(product.sizes).length > 12 && (
                      <SizeCount style={{ marginLeft: "4px" }}>
                        +{groupSizesByValue(product.sizes).length - 12}
                      </SizeCount>
                    )}
                </SizesContainer>
              </SizeLabel>
            </DesktopContent>
          );
        },
      },
      {
        id: "price",
        accessorKey: "priceRange",
        header: "Цена",
        cell: ({ row }) => {
          const product = row.original;
          return (
            <DesktopContent>
              <PriceLabel>
                {formatPrice(
                  product.priceRange.min === product.priceRange.max
                    ? `${product.priceRange.min} Р`
                    : `${product.priceRange.min} - ${product.priceRange.max} Р`
                )}
              </PriceLabel>
            </DesktopContent>
          );
        },
        sortingFn: (rowA, rowB) => {
          const avgPriceA = rowA.original.priceRange.min;
          const avgPriceB = rowB.original.priceRange.min;
          return avgPriceA - avgPriceB;
        },
      },
      {
        id: "profit",
        accessorKey: "avgProfit",
        header: "Прибыль",
        cell: ({ row }) => {
          const product = row.original;
          const profitColor = product.avgProfit >= 0 ? "#24BA7D" : "#FF5252";
          return (
            <DesktopContent>
              <PriceLabel style={{ color: profitColor }}>
                {formatPrice(`${product.avgProfit} Р`)}
                <span style={{ fontSize: "12px", marginLeft: "4px", opacity: 0.8 }}>
                  ({product.avgProfitPercent > 0 ? '+' : ''}{product.avgProfitPercent}%)
                </span>
              </PriceLabel>
            </DesktopContent>
          );
        },
        sortingFn: (rowA, rowB) => {
          return rowA.original.avgProfit - rowB.original.avgProfit;
        },
      },
      {
        id: "avgProfitPercent",
        accessorKey: "avgProfitPercent",
        header: "Ср. % прибыли",
        cell: ({ row }) => {
          const product = row.original;
          // Вычисляем средний процент прибыли из всех размеров
          if (!product.sizes || product.sizes.length === 0) {
            return (
              <DesktopContent>
                <PriceLabel>—</PriceLabel>
              </DesktopContent>
            );
          }

          let totalProfitPercent = 0;
          let validCount = 0;

          product.sizes.forEach((size: any) => {
            const cost = parseFloat(size.cost?.replace(/[^\d.-]/g, "") || "0");
            const price = parseFloat(
              size.price?.replace(/[^\d.-]/g, "") || "0"
            );
            if (cost > 0) {
              const profit = price - cost;
              const profitPercent = (profit / cost) * 100;
              totalProfitPercent += profitPercent;
              validCount++;
            }
          });

          const avgProfitPercent =
            validCount > 0 ? (totalProfitPercent / validCount).toFixed(1) : "0";
          const isPositive = parseFloat(avgProfitPercent) >= 0;

          return (
            <DesktopContent>
              <PriceLabel style={{ color: isPositive ? "#24BA7D" : "#E47171" }}>
                {isPositive ? "+" : ""}
                {avgProfitPercent}%
              </PriceLabel>
            </DesktopContent>
          );
        },
        sortingFn: (rowA, rowB) => {
          const calcAvgProfitPercent = (sizes: any[]) => {
            if (!sizes || sizes.length === 0) return 0;
            let totalProfitPercent = 0;
            let validCount = 0;
            sizes.forEach((size: any) => {
              const cost = parseFloat(
                size.cost?.replace(/[^\d.-]/g, "") || "0"
              );
              const price = parseFloat(
                size.price?.replace(/[^\d.-]/g, "") || "0"
              );
              if (cost > 0) {
                const profit = price - cost;
                totalProfitPercent += (profit / cost) * 100;
                validCount++;
              }
            });
            return validCount > 0 ? totalProfitPercent / validCount : 0;
          };

          const avgA = calcAvgProfitPercent(rowA.original.sizes);
          const avgB = calcAvgProfitPercent(rowB.original.sizes);
          return avgA - avgB;
        },
      },
      {
        id: "article",
        accessorKey: "article",
        header: "Артикул",
        cell: ({ row }) => {
          const product = row.original;
          return (
            <DesktopContent>
              <ArticleLabel>{product.article}</ArticleLabel>
            </DesktopContent>
          );
        },
      },
    ],
    [formatPrice]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const toggleRowExpansion = (articleId: string) => {
    setExpanded((prev) => ({
      ...prev,
      [articleId]: !prev[articleId],
    }));
  };

  if (isLoading && data.length === 0) {
    return (
      <div style={{ color: "#FFFFFF", textAlign: "center", padding: "24px 0" }}>
        Загрузка...
      </div>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <div style={{ color: "#FFFFFF", textAlign: "center", padding: "24px 0" }}>
        Нет проданных товаров.
      </div>
    );
  }

  return (
    <TableWrapper>
      <CardsContainer>
        <FilterRow>
          {table.getHeaderGroups()[0].headers.map((header) => (
            <FilterItem
              key={header.id}
              $clickable={header.column.getCanSort()}
              onClick={header.column.getToggleSortingHandler()}
            >
              <FilterLabel>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </FilterLabel>
              {header.column.getCanSort() && (
                <SortIcon
                  direction={
                    header.column.getIsSorted() === "asc"
                      ? "asc"
                      : header.column.getIsSorted() === "desc"
                      ? "desc"
                      : "none"
                  }
                />
              )}
            </FilterItem>
          ))}
        </FilterRow>

        {table.getRowModel().rows.map((row) => {
          const product = row.original;
          const isExpanded = expanded[product.article];

          return (
            <CardWithExpandWrapper key={product.article}>
              <CardContainer
                onClick={() => toggleRowExpansion(product.article)}
              >
                <CardRow>
                  {row.getVisibleCells().map((cell) => (
                    <React.Fragment key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </React.Fragment>
                  ))}
                </CardRow>

                <ExpandableContent $expanded={isExpanded}>
                  {Array.isArray(product.sizes) &&
                    product.sizes.map((size: any) => (
                      <SizeVariantContainer
                        key={size.id}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SizeVariantContent>
                          <SizeVariantInfo>
                            <div
                              style={{
                                marginBottom: "12px",
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                              }}
                            >
                              <div
                                style={{
                                  backgroundColor: "#0E1F1F",
                                  border: "1px solid #2C4A4A",
                                  borderRadius: "8px",
                                  padding: "8px 16px",
                                  fontSize: "16px",
                                  fontWeight: "600",
                                  color: "#FFFFFF",
                                  display: "inline-block",
                                  minWidth: "80px",
                                  textAlign: "center",
                                }}
                              >
                                {size.size}
                              </div>
                              <button
                                onClick={() => onDelete(size.id)}
                                title="Удалить товар"
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: "20px",
                                  height: "20px",
                                  padding: 0,
                                }}
                              >
                                <TrashIcon />
                              </button>
                            </div>
                            <SizeVariantGrid>
                              <InfoPill>
                                <InfoLabel>Покупатель</InfoLabel>
                                <InfoValue style={{ color: "#8B9BA8" }}>
                                  {size.buyerName || "—"}
                                </InfoValue>
                              </InfoPill>
                              <InfoPill>
                                <InfoLabel>В инвентаре</InfoLabel>
                                <InfoValue style={{ color: "#8B9BA8" }}>
                                  {(() => {
                                    if (!size.purchaseDate) return "—";
                                    const purchaseDate = new Date(
                                      size.purchaseDate
                                    );
                                    const saleDate = size.saleDate
                                      ? new Date(size.saleDate)
                                      : new Date();
                                    const diffTime = Math.abs(
                                      saleDate.getTime() -
                                        purchaseDate.getTime()
                                    );
                                    const diffDays = Math.ceil(
                                      diffTime / (1000 * 60 * 60 * 24)
                                    );
                                    return `${diffDays} ${
                                      diffDays === 1
                                        ? "день"
                                        : diffDays < 5
                                        ? "дня"
                                        : "дней"
                                    }`;
                                  })()}
                                </InfoValue>
                              </InfoPill>
                              <InfoPill>
                                <InfoLabel>Цена покупки</InfoLabel>
                                <InfoValue>
                                  {formatPrice(size.cost || "0 Р")}
                                </InfoValue>
                              </InfoPill>
                              <InfoPill>
                                <InfoLabel>Цена продажи</InfoLabel>
                                <InfoValue style={{ color: "#24BA7D" }}>
                                  {formatPrice(size.price || "0 Р")}
                                </InfoValue>
                              </InfoPill>
                              <InfoPill>
                                <InfoLabel>Прибыль</InfoLabel>
                                <InfoValue
                                  style={{
                                    color: (() => {
                                      const cost = parseFloat(
                                        size.cost?.replace(/[^\d.-]/g, "") ||
                                          "0"
                                      );
                                      const price = parseFloat(
                                        size.price?.replace(/[^\d.-]/g, "") ||
                                          "0"
                                      );
                                      const profit = price - cost;
                                      return profit >= 0
                                        ? "#24BA7D"
                                        : "#E47171";
                                    })(),
                                  }}
                                >
                                  {(() => {
                                    const cost = parseFloat(
                                      size.cost?.replace(/[^\d.-]/g, "") || "0"
                                    );
                                    const price = parseFloat(
                                      size.price?.replace(/[^\d.-]/g, "") || "0"
                                    );
                                    const profit = price - cost;
                                    return formatPrice(
                                      `${profit.toFixed(0)} Р`
                                    );
                                  })()}
                                </InfoValue>
                              </InfoPill>
                              <InfoPill>
                                <InfoLabel>Прибыль %</InfoLabel>
                                <InfoValue
                                  style={{
                                    color: (() => {
                                      const cost = parseFloat(
                                        size.cost?.replace(/[^\d.-]/g, "") ||
                                          "0"
                                      );
                                      const price = parseFloat(
                                        size.price?.replace(/[^\d.-]/g, "") ||
                                          "0"
                                      );
                                      const profit = price - cost;
                                      return profit >= 0
                                        ? "#24BA7D"
                                        : "#E47171";
                                    })(),
                                  }}
                                >
                                  {(() => {
                                    const cost = parseFloat(
                                      size.cost?.replace(/[^\d.-]/g, "") || "0"
                                    );
                                    const price = parseFloat(
                                      size.price?.replace(/[^\d.-]/g, "") || "0"
                                    );
                                    const profit = price - cost;
                                    const profitPercent =
                                      cost > 0
                                        ? ((profit / cost) * 100).toFixed(1)
                                        : "0.0";
                                    return `${
                                      profit >= 0 ? "+" : ""
                                    }${profitPercent}%`;
                                  })()}
                                </InfoValue>
                              </InfoPill>
                              <InfoPill>
                                <InfoLabel>Подробнее</InfoLabel>
                                <ProductLink
                                  to={`/sneaker/${size.id}`}
                                  state={{ from: "sales" }}
                                  style={{ fontSize: '12px', padding: '2px 0' }}
                                >
                                  Открыть →
                                </ProductLink>
                              </InfoPill>
                            </SizeVariantGrid>
                          </SizeVariantInfo>
                        </SizeVariantContent>
                      </SizeVariantContainer>
                    ))}
                </ExpandableContent>
              </CardContainer>
            </CardWithExpandWrapper>
          );
        })}
      </CardsContainer>
    </TableWrapper>
  );
};

const TrashIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.5 4.98333C14.725 4.70833 11.9333 4.56667 9.15 4.56667C7.5 4.56667 5.85 4.65 4.2 4.81667L2.5 4.98333"
      stroke="#E47171"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.08333 4.14167L7.26666 3.05C7.39999 2.25833 7.5 1.66667 8.90833 1.66667H11.0917C12.5 1.66667 12.6083 2.29167 12.7333 3.05833L12.9167 4.14167"
      stroke="#E47171"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.7084 7.61667L15.1667 16.0083C15.075 17.3167 15 18.3333 12.675 18.3333H7.32502C5.00002 18.3333 4.92502 17.3167 4.83336 16.0083L4.29169 7.61667"
      stroke="#E47171"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.60834 13.75H11.3833"
      stroke="#E47171"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.91669 10.4167H12.0834"
      stroke="#E47171"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default SalesTable;
